import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Search, X as XIcon } from 'lucide-react';
import { api } from '@/api/client';

interface Courier { id: number; name: string; code: string; brand?: string; orderType: 'B2C' | 'B2B'; serviceType: string }

interface Props {
  packId?: number;
  onSuccess: () => void;
}

export function CredentialPackForm({ packId, onSuccess }: Props) {
  const qc = useQueryClient();
  const isEdit = !!packId;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [credText, setCredText] = useState('{\n  "username": "",\n  "password": "",\n  "api_key": ""\n}');
  const [courierFilter, setCourierFilter] = useState('');
  const [orderTypeFilter, setOrderTypeFilter] = useState<'all' | 'B2C' | 'B2B'>('all');

  // Load all couriers for the picker
  const { data: couriers } = useQuery({
    queryKey: ['couriers', 'all-for-credentials'],
    queryFn: async () => (await api.get<Courier[]>('/couriers', { params: { isActive: 'true' } })).data,
    staleTime: 1000 * 60 * 5,
  });

  // Load existing pack for edit (with credentials revealed)
  const { data: existing } = useQuery({
    queryKey: ['credential-pack', packId],
    queryFn: async () => (await api.get<any>(`/credential-packs/${packId}?reveal=true`)).data,
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing) {
      setName(existing.name ?? '');
      setDescription(existing.description ?? '');
      setIsActive(existing.isActive !== false);
      setIsLive(!!existing.isLive);
      setSelectedIds(new Set(existing.courierIds ?? []));
      setCredText(JSON.stringify(existing.credentials ?? {}, null, 2));
    }
  }, [existing]);

  const save = useMutation({
    mutationFn: async () => {
      let credentials: any = {};
      try { credentials = JSON.parse(credText); }
      catch (e: any) { throw new Error('Credentials JSON is invalid: ' + e.message); }
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        courierIds: Array.from(selectedIds),
        isActive, isLive,
        credentials,
      };
      if (!payload.name) throw new Error('Name is required');
      if (payload.courierIds.length === 0) throw new Error('Select at least one courier');
      if (isEdit) return (await api.patch(`/credential-packs/${packId}`, payload)).data;
      return (await api.post('/credential-packs', payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credential-packs'] });
      qc.invalidateQueries({ queryKey: ['credential-pack', packId] });
      toast.success(isEdit ? 'Credential pack updated' : 'Credential pack created');
      onSuccess();
    },
    onError: (e: any) => toast.error(e?.message ?? e?.response?.data?.message ?? 'Save failed'),
  });

  const filteredCouriers = (couriers ?? []).filter(c => {
    if (orderTypeFilter !== 'all' && c.orderType !== orderTypeFilter) return false;
    if (courierFilter) {
      const q = courierFilter.toLowerCase();
      if (!c.name.toLowerCase().includes(q) && !c.code.toLowerCase().includes(q) && !(c.brand ?? '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const toggle = (id: number) => setSelectedIds(prev => {
    const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next;
  });

  const selectAllVisible = () => setSelectedIds(prev => {
    const next = new Set(prev); for (const c of filteredCouriers) next.add(c.id); return next;
  });
  const clearSelection = () => setSelectedIds(new Set());

  return (
    <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Pack Name *">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="DTDC Noida (NL3991)" className="input" />
        </Field>
        <Field label="Description">
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Shared between Surface & Express" className="input" />
        </Field>
      </div>

      <div className="flex items-center gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="rounded" /> Active
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={isLive} onChange={(e) => setIsLive(e.target.checked)} className="rounded" />
          Live (production) — unchecked = sandbox/test
        </label>
      </div>

      {/* Couriers picker */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="p-3 bg-slate-50/50 border-b border-slate-200 flex items-center gap-2 flex-wrap">
          <div className="text-sm font-semibold text-slate-900 flex-1">
            Couriers this pack applies to <span className="text-xs text-slate-500 font-normal">({selectedIds.size} selected)</span>
          </div>
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1">
            <Search size={14} className="text-slate-400" />
            <input value={courierFilter} onChange={(e) => setCourierFilter(e.target.value)} placeholder="Filter couriers..."
              className="text-sm outline-none w-48" />
          </div>
          <select value={orderTypeFilter} onChange={(e) => setOrderTypeFilter(e.target.value as any)}
            className="px-2 py-1 border border-slate-200 rounded text-sm">
            <option value="all">All types</option>
            <option value="B2C">B2C</option>
            <option value="B2B">B2B</option>
          </select>
          <button type="button" onClick={selectAllVisible} className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-100">Select visible</button>
          <button type="button" onClick={clearSelection} className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-100">Clear</button>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {filteredCouriers.length === 0 && <div className="text-center py-6 text-slate-400 text-sm">No couriers match this filter.</div>}
          {filteredCouriers.map(c => (
            <label key={c.id} className={`flex items-center gap-3 px-3 py-2 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${selectedIds.has(c.id) ? 'bg-primary/5' : ''}`}>
              <input type="checkbox" checked={selectedIds.has(c.id)} onChange={() => toggle(c.id)} className="rounded" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{c.name} <span className="text-xs text-slate-400 font-mono">#{c.id}</span></div>
                <div className="text-xs text-slate-500 flex items-center gap-2">
                  <code className="bg-slate-100 px-1 rounded">{c.code}</code>
                  <span className={`px-1.5 rounded ${c.orderType === 'B2C' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>{c.orderType}</span>
                  <span>{c.serviceType}</span>
                  {c.brand && <span className="text-slate-400">· {c.brand}</span>}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Selected chips */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap gap-1">
          {Array.from(selectedIds).map(id => {
            const c = (couriers ?? []).find(x => x.id === id);
            return (
              <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs">
                {c?.name ?? `#${id}`} <span className="opacity-50 font-mono">#{id}</span>
                <button type="button" onClick={() => toggle(id)} className="hover:text-danger"><XIcon size={12} /></button>
              </span>
            );
          })}
        </div>
      )}

      <Field label="Credentials (JSON)">
        <p className="text-xs text-slate-500 mb-1">
          Keys vary per courier. For DTDC: <code>api_key</code>, <code>code</code>, <code>username</code>, <code>password</code>, <code>tracking_token</code>.
          For Delhivery: <code>token</code>, <code>client</code>, <code>user</code>.
          Encrypted at rest with AES-256-GCM.
        </p>
        <textarea value={credText} onChange={(e) => setCredText(e.target.value)} rows={9} className="input font-mono text-xs" />
      </Field>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 sticky bottom-0 bg-white pb-1">
        <button type="button" onClick={onSuccess} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button type="button" onClick={() => save.mutate()} disabled={save.isPending}
          className="px-4 py-2 bg-primary text-white text-sm rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2">
          {save.isPending && <Loader2 size={14} className="animate-spin" />}
          {isEdit ? 'Save Changes' : 'Create Credential Pack'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
